import {
  keepPreviousData,
  QueryCache,
  QueryClient,
} from "@tanstack/react-query";
import { ApiError, BASE } from "./api";
import type { Identity } from "./auth";
import { mark } from "./performance";

export const SNAPSHOT_KEY = "neulytics-dashboard-v1";
export const MAX_AGE_MS = 30 * 60_000;
// UTF-16 storage: leave room below common per-origin quotas.
const MAX_CHARS = 1_500_000;
const ALLOWED = new Set([
  "games",
  "pulse",
  "overview",
  "overview-report",
  "sessions",
  "retention",
  "retention-cohorts",
  "sessions-comparison",
  "engagement-daily",
  "engagement-daily-previous",
  "audience-comparison",
  "performance-comparison",
  "roblox-retention-history",
  "audience",
  "audience-report",
  "audience-actives",
  "audience-demographics",
  "funnels",
  "funnel",
  "journeys",
  "journey",
  "product",
  "product-dashboard",
  "product-dashboards",
  "notifications",
  "economy",
  "currency",
  "performance",
  "parity",
  "experiments",
  "experiment",
  "funnels-for-experiments",
  "roblox-retention-breakdown",
  "roblox-capabilities",
  "discovery",
  "discovery-previous",
]);
type StorageAccess = Pick<Storage, "getItem" | "setItem" | "removeItem">;
interface SnapshotEntry {
  key: readonly unknown[];
  data: unknown;
  updatedAt: number;
}

export function canKeepData(error: unknown): boolean {
  // Permission changes and invalid filters are not transient outages. Never
  // keep showing a result that the current request is no longer allowed to read.
  return !(
    error instanceof ApiError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

/** Receiving a saved answer does not make its calculation new again. */
export function freshFor(data: unknown, receivedAt: number, cadence: number): number {
  const meta = typeof data === "object" && data !== null && "meta" in data ? data.meta : null;
  if (typeof meta !== "object" || meta === null) return cadence;
  if ("refreshState" in meta && (meta.refreshState === "refreshing" || meta.refreshState === "failed")) return 0;
  const calculated = "computedAt" in meta ? Date.parse(String(meta.computedAt)) : NaN;
  return Number.isFinite(calculated) ? Math.max(0, cadence - Math.max(0, receivedAt - calculated)) : cadence;
}

export function createDashboardClient(): QueryClient {
  const refreshPolls = new WeakSet<object>();
  const client = new QueryClient({
    queryCache: new QueryCache({
      onSuccess: (data, query) => {
        requestAnimationFrame(() => mark("rendered-answer"));
        const meta = typeof data === "object" && data !== null && "meta" in data ? data.meta : undefined;
        if (typeof meta === "object" && meta !== null && "refreshState" in meta && meta.refreshState === "refreshing" && !refreshPolls.has(query)) {
          refreshPolls.add(query);
          window.setTimeout(() => {
            refreshPolls.delete(query);
            if (query.isActive()) void client.refetchQueries({ queryKey: query.queryKey, exact: true, type: "active" });
          }, 2_000);
        }
      },
    }),
    defaultOptions: {
      queries: {
        refetchInterval: 30_000,
        gcTime: MAX_AGE_MS,
        placeholderData: keepPreviousData,
        staleTime: (query) => {
          const cadence: unknown = Reflect.get(
            query.options,
            "refetchInterval",
          );
          return freshFor(query.state.data, query.state.dataUpdatedAt, typeof cadence === "number" ? cadence : 30_000);
        },
        retry: (attempts, error) =>
          error instanceof ApiError ? false : attempts < 1,
        refetchOnWindowFocus: true,
      },
    },
  });
  return client;
}

function owner(identity: Identity): string {
  return JSON.stringify([BASE, identity.email, identity.role]);
}
function eligible(
  key: readonly unknown[],
  data: unknown,
  identity: Identity,
): boolean {
  if (typeof key[0] !== "string" || !ALLOWED.has(key[0])) return false;
  if (
    !data ||
    typeof data !== "object" ||
    !("meta" in data) ||
    !("data" in data)
  )
    return false;
  const meta = data.meta;
  return (
    !!meta &&
    typeof meta === "object" &&
    "tier" in meta &&
    meta.tier === identity.role
  );
}

export function clearSnapshot(storage: StorageAccess): void {
  try {
    storage.removeItem(SNAPSHOT_KEY);
  } catch {
    /* Storage can be disabled. */
  }
}

/** Restore only AFTER /v1/me has verified this exact account and role. */
export function restoreSnapshot(
  client: QueryClient,
  storage: StorageAccess,
  identity: Identity,
  now = Date.now(),
): void {
  try {
    const raw = storage.getItem(SNAPSHOT_KEY);
    if (!raw) return;
    if (raw.length > MAX_CHARS) {
      clearSnapshot(storage);
      return;
    }
    const snapshot = JSON.parse(raw) as {
      version?: number;
      owner?: string;
      entries?: SnapshotEntry[];
    };
    if (
      // Old saved sessions do not contain the completed-session measurement.
      snapshot.version !== 2 ||
      snapshot.owner !== owner(identity) ||
      !Array.isArray(snapshot.entries)
    ) {
      clearSnapshot(storage);
      return;
    }
    for (const entry of snapshot.entries.slice(0, 24)) {
      if (
        !Array.isArray(entry.key) ||
        !Number.isFinite(entry.updatedAt) ||
        entry.updatedAt > now ||
        now - entry.updatedAt >= MAX_AGE_MS ||
        !eligible(entry.key, entry.data, identity)
      )
        continue;
      client.setQueryData(entry.key, entry.data, {
        updatedAt: entry.updatedAt,
      });
      // A reload gets an immediate saved answer AND a fresh read, even if the
      // snapshot was written inside the usual polling interval.
      void client.invalidateQueries({
        queryKey: entry.key,
        exact: true,
        refetchType: "none",
      });
    }
  } catch {
    clearSnapshot(storage);
  }
}

export function saveSnapshot(
  client: QueryClient,
  storage: StorageAccess,
  identity: Identity,
  now = Date.now(),
): void {
  const entries: string[] = [];
  let size = 0;
  for (const query of client
    .getQueryCache()
    .getAll()
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)) {
    const { data, dataUpdatedAt, error } = query.state;
    if (
      now - dataUpdatedAt >= MAX_AGE_MS ||
      !canKeepData(error) ||
      !eligible(query.queryKey, data, identity)
    )
      continue;
    const entry = { key: query.queryKey, data, updatedAt: dataUpdatedAt };
    try {
      const serialized = JSON.stringify(entry);
      const chars = serialized.length;
      if (size + chars > MAX_CHARS - 1000) continue;
      entries.push(serialized);
      size += chars;
      if (entries.length === 24) break;
    } catch {
      /* One non-serializable result must not break the dashboard. */
    }
  }
  try {
    storage.setItem(
      SNAPSHOT_KEY,
      `{"version":2,"owner":${JSON.stringify(owner(identity))},"entries":[${entries.join(",")}]}`,
    );
  } catch {
    /* Quota/private-mode failure falls back to the in-memory cache. */
  }
}
